import React, { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    const saved = localStorage.getItem('littlelearners-theme');
    return saved || 'kids';
  });
  
  const [colorMode, setColorMode] = useState(() => {
    const saved = localStorage.getItem('littlelearners-color-mode');
    return saved || 'light';
  });

  const [accessibility, setAccessibility] = useState(() => {
    const saved = localStorage.getItem('littlelearners-accessibility');
    return saved ? JSON.parse(saved) : {
      highContrast: false,
      largeText: false,
      reducedMotion: false,
      voiceEnabled: true,
      ttsEnabled: true
    };
  });

  useEffect(() => {
    localStorage.setItem('littlelearners-theme', theme);
    localStorage.setItem('littlelearners-color-mode', colorMode);
    localStorage.setItem('littlelearners-accessibility', JSON.stringify(accessibility));
    
    // Apply theme classes to document
    const root = document.documentElement;
    root.classList.remove('theme-kids', 'theme-parents', 'dark', 'high-contrast', 'large-text');
    
    root.classList.add(`theme-${theme}`);
    if (colorMode === 'dark') root.classList.add('dark');
    if (accessibility.highContrast) root.classList.add('high-contrast');
    if (accessibility.largeText) root.classList.add('large-text');
    
    if (accessibility.reducedMotion) {
      root.style.setProperty('--animation-duration', '0ms');
    } else {
      root.style.removeProperty('--animation-duration');
    }
  }, [theme, colorMode, accessibility]);

  const toggleTheme = () => {
    setTheme(prev => prev === 'kids' ? 'parents' : 'kids');
  };

  const toggleColorMode = () => {
    setColorMode(prev => prev === 'light' ? 'dark' : 'light');
  };

  const updateAccessibility = (key, value) => {
    setAccessibility(prev => ({ ...prev, [key]: value }));
  };

  return (
    <ThemeContext.Provider value={{
      theme,
      setTheme,
      colorMode,
      setColorMode,
      toggleTheme,
      toggleColorMode,
      accessibility,
      updateAccessibility,
      isKidsMode: theme === 'kids',
      isDarkMode: colorMode === 'dark'
    }}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeProvider;
